import React, { useState, useEffect } from 'react';
import {
  Modal,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Divider,
  message,
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Alert,
} from 'antd';
import {
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

const { Title, Text } = Typography;

dayjs.locale('fr');

interface LineItem {
  id: number;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
  item_type: 'subscription' | 'addon' | 'discount' | 'tax' | 'other';
}

interface InvoiceDetail {
  id: number;
  invoice_number: string;
  company_id: number;
  company_name: string;
  company_email: string;
  company_phone?: string;
  company_address?: string;
  plan_name: string;
  plan_description?: string;
  invoice_date: string;
  due_date: string;
  period_start: string;
  period_end: string;
  subtotal: number;
  discount_amount: number;
  tax_amount: number;
  total_amount: number;
  paid_amount?: number;
  currency: string;
  status: 'draft' | 'issued' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  payment_status: 'unpaid' | 'partial' | 'paid' | 'refunded';
  payment_date?: string;
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
  correction_reason?: string;
  replaces_invoice_number?: string;
  replacement_invoice_number?: string;
  line_items: LineItem[];
}

interface InvoiceDetailModalProps {
  visible: boolean;
  invoice: any; // Invoice basique de la liste
  onClose: () => void;
  onRefresh?: () => void;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  visible,
  invoice,
  onClose,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetail | null>(null);

  // Fonctions utilitaires pour formater les montants avec séparateurs de milliers
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined, currency?: string): string => {
    const formatted = formatAmount(amount);
    return currency ? `${formatted} ${currency}` : `${formatted} XOF`;
  };

  // Charger les détails complets de la facture
  useEffect(() => {
    if (visible && invoice?.id) {
      fetchInvoiceDetail();
    }
  }, [visible, invoice]);

  const fetchInvoiceDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/subscription/invoices/${invoice.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setInvoiceDetail(data.data.invoice);
      } else {
        message.error('Erreur lors du chargement des détails');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le PDF (Backend - React-PDF - PATTERN EXACT DES VENTES)
  const handleDownloadPDF = async () => {
    try {
      if (!invoiceDetail) {
        message.error('Aucune facture sélectionnée');
        return;
      }

      message.loading({ content: 'Génération du PDF en cours...', key: 'pdfGen', duration: 0 });

      const fileName = `Facture-${invoiceDetail.invoice_number}.pdf`;

      // Le serveur charge la facture officielle et prépare son QR.
      const response = await fetch('/api/subscription/invoices/generate-pdf-react', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          invoiceId: invoiceDetail.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la génération du PDF');
      }

      // Télécharger le PDF (PATTERN EXACT DES VENTES)
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Facture téléchargée avec succès', key: 'pdfGen' });
    } catch (error) {
      console.error('❌ Erreur génération PDF:', error);
      message.error({
        content: error instanceof Error ? error.message : 'Erreur lors du téléchargement du PDF',
        key: 'pdfGen'
      });
    }
  };

  // Marquer comme payée
  const handleMarkAsPaid = async () => {
    Modal.confirm({
      title: 'Marquer comme payée',
      content: `Êtes-vous sûr de vouloir marquer la facture ${invoice.invoice_number} comme payée ?`,
      okText: 'Oui, marquer comme payée',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const response = await fetch(`/api/subscription/invoices/${invoice.id}/mark-paid`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              payment_date: new Date().toISOString(),
              payment_method: 'manual',
              notes: 'Marquée comme payée manuellement',
            }),
          });

          const data = await response.json();

          if (data.success) {
            message.success(data.message || 'Facture marquée comme payée');
            fetchInvoiceDetail();
            if (onRefresh) onRefresh();
          } else {
            message.error(data.message || 'Erreur lors de la mise à jour');
          }
        } catch (error) {
          console.error('Erreur:', error);
          message.error('Erreur de connexion');
        }
      },
    });
  };

  // Colonnes du tableau des lignes de facture
  const lineItemsColumns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: LineItem) => (
        <div>
          <div>{text}</div>
          {record.item_type !== 'subscription' && (
            <Tag color="blue" style={{ fontSize: '11px' }}>{record.item_type}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center' as const,
    },
    {
      title: 'Prix unitaire',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 150,
      align: 'right' as const,
      render: (value: number, record: LineItem) => (
        formatCurrency(value, invoiceDetail?.currency)
      ),
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right' as const,
      render: (value: number, record: LineItem) => (
        <strong>{formatCurrency(value, invoiceDetail?.currency)}</strong>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center justify-between">
          <span>
            <FileTextOutlined className="mr-2" />
            Détails de la Facture
          </span>
          {invoiceDetail && (
            <InvoiceStatusBadge
              status={invoiceDetail.status}
              paymentStatus={invoiceDetail.payment_status}
            />
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        invoiceDetail?.payment_status !== 'paid' && invoiceDetail?.status !== 'cancelled' && (
          <Button
            key="mark-paid"
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={handleMarkAsPaid}
          >
            Marquer comme payée
          </Button>
        ),
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPDF}
        >
          Télécharger PDF
        </Button>,
      ]}
    >
      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" />
        </div>
      ) : invoiceDetail ? (
        <div>
          {/* En-tête avec numéro de facture */}
          <Card className="mb-4 bg-blue-50">
            <Row gutter={[16, 16]} align="middle">
              <Col span={12}>
                <Title level={3} className="mb-0">
                  {invoiceDetail.invoice_number}
                </Title>
                <Text type="secondary">{invoiceDetail.plan_name}</Text>
              </Col>
              <Col span={12} className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(invoiceDetail.total_amount, invoiceDetail.currency)}
                </div>
                <Text type="secondary">Montant total TTC</Text>
              </Col>
            </Row>
          </Card>

          {invoiceDetail.replaces_invoice_number && (
            <Alert
              message={`Cette facture remplace ${invoiceDetail.replaces_invoice_number}`}
              description={invoiceDetail.correction_reason || undefined}
              type="info"
              showIcon
              className="mb-4"
            />
          )}
          {invoiceDetail.replacement_invoice_number && (
            <Alert
              message={`Facture annulée et remplacée par ${invoiceDetail.replacement_invoice_number}`}
              description={invoiceDetail.correction_reason || undefined}
              type="warning"
              showIcon
              className="mb-4"
            />
          )}

          {/* Alerte de paiement si payée */}
          {invoiceDetail.payment_status === 'paid' && invoiceDetail.payment_date && (
            <Alert
              message="Facture Payée"
              description={
                <div>
                  <p><strong>Date de paiement:</strong> {dayjs(invoiceDetail.payment_date).format('DD MMMM YYYY')}</p>
                  {invoiceDetail.payment_method && (
                    <p><strong>Méthode:</strong> {invoiceDetail.payment_method}</p>
                  )}
                  {invoiceDetail.payment_reference && (
                    <p><strong>Référence:</strong> {invoiceDetail.payment_reference}</p>
                  )}
                </div>
              }
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              className="mb-4"
            />
          )}

          {/* Informations client et dates */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col span={12}>
              <Card title="Informations Client" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Entreprise">
                    <strong>{invoiceDetail.company_name}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {invoiceDetail.company_email}
                  </Descriptions.Item>
                  {invoiceDetail.company_phone && (
                    <Descriptions.Item label="Téléphone">
                      {invoiceDetail.company_phone}
                    </Descriptions.Item>
                  )}
                  {invoiceDetail.company_address && (
                    <Descriptions.Item label="Adresse">
                      {invoiceDetail.company_address}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="Dates" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Date d'émission">
                    <CalendarOutlined className="mr-2" />
                    {dayjs(invoiceDetail.invoice_date).format('DD MMMM YYYY')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date d'échéance">
                    <CalendarOutlined className="mr-2" />
                    <strong className={
                      invoiceDetail.status === 'overdue' ? 'text-red-500' : ''
                    }>
                      {dayjs(invoiceDetail.due_date).format('DD MMMM YYYY')}
                    </strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Période de facturation">
                    {dayjs(invoiceDetail.period_start).format('DD/MM/YYYY')} - {dayjs(invoiceDetail.period_end).format('DD/MM/YYYY')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>

          <Divider>Détails de la Facture</Divider>

          {/* Lignes de facture */}
          <Table
            columns={lineItemsColumns}
            dataSource={invoiceDetail.line_items}
            pagination={false}
            rowKey="id"
            size="small"
            className="mb-4"
          />

          {/* Totaux */}
          <Row justify="end">
            <Col span={10}>
              <Card size="small">
                <div className="flex justify-between mb-2">
                  <Text>Sous-total:</Text>
                  <Text strong>
                    {formatCurrency(invoiceDetail.subtotal, invoiceDetail.currency)}
                  </Text>
                </div>

                {invoiceDetail.discount_amount > 0 && (
                  <div className="flex justify-between mb-2 text-orange-500">
                    <Text>Remise:</Text>
                    <Text strong>
                      -{formatCurrency(invoiceDetail.discount_amount, invoiceDetail.currency)}
                    </Text>
                  </div>
                )}

                {invoiceDetail.tax_amount > 0 && (
                  <div className="flex justify-between mb-2">
                    <Text>TVA:</Text>
                    <Text strong>
                      {formatCurrency(invoiceDetail.tax_amount, invoiceDetail.currency)}
                    </Text>
                  </div>
                )}

                <Divider className="my-2" />

                <div className="flex justify-between">
                  <Text strong style={{ fontSize: '16px' }}>TOTAL TTC:</Text>
                  <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
                    {formatCurrency(invoiceDetail.total_amount, invoiceDetail.currency)}
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Notes */}
          {invoiceDetail.notes && (
            <div className="mt-4">
              <Divider>Notes</Divider>
              <Card size="small">
                <Text>{invoiceDetail.notes}</Text>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Text type="secondary">Aucune facture sélectionnée</Text>
        </div>
      )}
    </Modal>
  );
};

export default InvoiceDetailModal;
